/**
 * Existing "Charger activity" event timeline, embedded as a tab.
 *
 * The body lives in this file (originally on `ChargerEventLogsScreen`) so
 * we can drop it into the tabbed page without duplicating event-formatting
 * logic. The dedicated `/ChargerEventLogs` route stays in place to keep
 * existing notification deep-links working.
 *
 * Endpoint: GET charger/events/:chargerId — see
 * `src/services/charger-service.tsx#getChargerEvents`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import OcppEmptyState from '../components/OcppEmptyState';
import OcppErrorBanner from '../components/OcppErrorBanner';
import { OcppTheme } from '../components/OcppTheme';
import { mapOcppApiError, type OcppApiErrorInfo } from '../utils/apiErrors';
import { isSuccessful } from '../../services/api-requests';
import { getChargerEvents } from '../../services/charger-service';
import type { ChargerEventRecord } from '../../models/charger-event-model';
import type { OcppTabProps } from './types';

const EVENT_LABELS: Record<string, string> = {
  boot: 'Charger online',
  status_change: 'Status change',
  fault: 'Fault',
  fault_recovery: 'Fault cleared',
  diagnostics: 'Diagnostics',
  firmware_update: 'Firmware',
  security: 'Security',
};

const DEFAULT_FETCH_LIMIT = 150;

const metaString = (m: Record<string, unknown>, key: string): string | undefined => {
  const v = m[key];
  if (v === undefined || v === null) return undefined;
  return String(v);
};

const eventTitle = (event: ChargerEventRecord): string =>
  EVENT_LABELS[event.event_type] ?? event.event_type.replace(/_/g, ' ');

const eventSubtitle = (event: ChargerEventRecord): string => {
  const m = event.metadata || {};
  const status = metaString(m, 'status');
  const err = metaString(m, 'error_code');
  const userMsg = metaString(m, 'user_message');
  const connector = metaString(m, 'connector_id');
  const parts: string[] = [];
  if (connector !== undefined) parts.push(`Connector ${connector}`);
  if (status) parts.push(status);
  if (err) parts.push(err);
  if (userMsg && event.event_type === 'fault') parts.push(userMsg);
  return parts.length ? parts.slice(0, 3).join(' · ') : '';
};

const isFaultLikeStatus = (status: string | undefined): boolean => {
  if (!status) return false;
  const s = status.toLowerCase();
  return s.includes('fault');
};

const eventSeverity = (event: ChargerEventRecord): 'error' | 'warning' | 'default' => {
  const m = event.metadata || {};
  const sev = metaString(m, 'error_severity')?.toLowerCase();
  if (event.event_type === 'fault' || sev === 'high' || sev === 'critical') return 'error';
  if (event.event_type === 'fault_recovery') return 'default';
  const status = metaString(m, 'status');
  if (event.event_type === 'status_change' && isFaultLikeStatus(status)) return 'error';
  if (event.event_type === 'status_change' && status && /fault|error|unavailable/i.test(status))
    return 'warning';
  if (metaString(m, 'error_code')) return 'warning';
  return 'default';
};

const formatEventTime = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatMetadataRows = (metadata: Record<string, unknown>): { key: string; value: string }[] => {
  const keys = Object.keys(metadata).sort((a, b) => a.localeCompare(b));
  return keys.map((key) => {
    const val = metadata[key];
    let value: string;
    if (val === null || val === undefined) value = '—';
    else if (typeof val === 'object') {
      try {
        value = JSON.stringify(val, null, 2);
      } catch {
        value = String(val);
      }
    } else value = String(val);
    return { key, value };
  });
};

const ActivityLogTab: React.FC<OcppTabProps> = ({ chargerId, isActive }) => {
  const [events, setEvents] = useState<ChargerEventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<OcppApiErrorInfo | null>(null);
  const [detailEvent, setDetailEvent] = useState<ChargerEventRecord | null>(null);

  const load = useCallback(async () => {
    if (!chargerId) {
      setError({
        title: 'Missing charger',
        message: 'No charger was selected.',
        blockedByGate: false,
        featureGated: false,
        status: undefined,
      });
      setLoading(false);
      return;
    }
    const response = await getChargerEvents(chargerId, DEFAULT_FETCH_LIMIT);
    if (isSuccessful(response.status) && response.payload?.events) {
      setEvents(response.payload.events);
      setError(null);
    } else {
      setEvents([]);
      setError(mapOcppApiError(response.status, response.payload));
    }
  }, [chargerId]);

  useEffect(() => {
    if (!isActive) return;
    let alive = true;
    (async () => {
      setLoading(true);
      await load();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [load, isActive]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  if (loading && !refreshing) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={OcppTheme.accent} />
        <Text style={styles.loadingText}>Loading activity…</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.bannerWrap}>
        <OcppErrorBanner error={error} onRetry={onRefresh} />
      </View>
    );
  }

  if (events.length === 0) {
    return (
      <OcppEmptyState
        icon="timeline"
        title="No activity yet"
        description="When your charger connects and reports status changes, entries will show up here."
      />
    );
  }

  return (
    <View style={styles.flexFill}>
      <FlatList
        data={events}
        keyExtractor={(item) => `evt-${item.id}`}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={OcppTheme.accent}
            colors={[OcppTheme.accent]}
          />
        }
        renderItem={({ item }) => {
          const severity = eventSeverity(item);
          const accent =
            severity === 'error' ? OcppTheme.danger : severity === 'warning' ? '#DD9A3C' : OcppTheme.accent;
          const sub = eventSubtitle(item);
          return (
            <Pressable
              style={({ pressed }) => [styles.row, pressed && { opacity: 0.85 }]}
              onPress={() => setDetailEvent(item)}
            >
              <View style={[styles.rowAccent, { backgroundColor: accent }]} />
              <View style={styles.rowBody}>
                <View style={styles.rowHeader}>
                  <Text style={styles.rowTitle}>{eventTitle(item)}</Text>
                  <MaterialIcons name="chevron-right" size={22} color="#5A5D62" />
                </View>
                {sub ? (
                  <Text style={styles.rowSubtitle} numberOfLines={2}>
                    {sub}
                  </Text>
                ) : null}
                <Text style={styles.rowTime}>{formatEventTime(item.timestamp)}</Text>
              </View>
            </Pressable>
          );
        }}
      />

      <Modal
        visible={detailEvent !== null}
        animationType="slide"
        presentationStyle={Platform.OS === 'web' ? undefined : 'pageSheet'}
        onRequestClose={() => setDetailEvent(null)}
      >
        <SafeAreaView style={styles.modalSafe}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {detailEvent ? eventTitle(detailEvent) : ''}
            </Text>
            <Pressable
              hitSlop={12}
              onPress={() => setDetailEvent(null)}
              accessibilityRole="button"
              accessibilityLabel="Close details"
            >
              <MaterialIcons name="close" size={26} color="#E5E7EB" />
            </Pressable>
          </View>
          <ScrollView style={styles.modalScroll} contentContainerStyle={styles.modalScrollContent}>
            {detailEvent ? (
              <>
                <Text style={styles.modalMetaLine}>{formatEventTime(detailEvent.timestamp)}</Text>
                <Text style={styles.modalMetaDim}>Event ID {detailEvent.id}</Text>
                <Text style={styles.sectionLabel}>Details</Text>
                {formatMetadataRows(detailEvent.metadata || {}).map((r) => (
                  <View key={r.key} style={styles.kvRow}>
                    <Text style={styles.kvKey}>{r.key}</Text>
                    <Text style={styles.kvVal} selectable>
                      {r.value}
                    </Text>
                  </View>
                ))}
                <Text style={styles.sectionLabel}>Raw metadata (JSON)</Text>
                <Text style={styles.rawJson} selectable>
                  {JSON.stringify(detailEvent.metadata ?? {}, null, 2)}
                </Text>
              </>
            ) : null}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </View>
  );
};

export default ActivityLogTab;

const styles = StyleSheet.create({
  flexFill: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  bannerWrap: {
    paddingTop: 4,
  },
  centered: {
    paddingVertical: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    color: OcppTheme.textMuted,
    fontSize: 14,
  },
  listContent: {
    paddingBottom: 24,
    paddingTop: 4,
  },
  row: {
    flexDirection: 'row',
    backgroundColor: OcppTheme.surface,
    borderRadius: 12,
    marginBottom: 10,
    overflow: 'hidden',
  },
  rowAccent: {
    width: 4,
  },
  rowBody: {
    flex: 1,
    paddingVertical: 12,
    paddingRight: 10,
    paddingLeft: 12,
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rowTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: '#F3F4F6',
    textTransform: 'capitalize',
  },
  rowSubtitle: {
    marginTop: 4,
    fontSize: 13,
    color: '#D1D5DB',
    lineHeight: 18,
  },
  rowTime: {
    marginTop: 8,
    fontSize: 12,
    color: OcppTheme.textDim,
  },
  modalSafe: {
    flex: 1,
    backgroundColor: '#111418',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: OcppTheme.border,
  },
  modalTitle: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: OcppTheme.textPrimary,
    marginRight: 12,
  },
  modalScroll: {
    flex: 1,
  },
  modalScrollContent: {
    padding: 18,
    paddingBottom: 40,
  },
  modalMetaLine: {
    fontSize: 15,
    color: '#E5E7EB',
    marginBottom: 4,
  },
  modalMetaDim: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: OcppTheme.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 8,
    marginBottom: 10,
  },
  kvRow: {
    marginBottom: 12,
  },
  kvKey: {
    fontSize: 12,
    color: OcppTheme.textMuted,
    marginBottom: 4,
  },
  kvVal: {
    fontSize: 14,
    color: '#F3F4F6',
    lineHeight: 20,
  },
  rawJson: {
    fontFamily: Platform.select({ ios: 'Menlo', android: 'monospace', web: 'monospace' }),
    fontSize: 12,
    color: '#D1D5DB',
    backgroundColor: '#1A1D21',
    padding: 12,
    borderRadius: 8,
    overflow: 'hidden',
  },
});
